/**
 * cryptoV2 - chunked AES-256-GCM file encryption (WebCrypto, no dependencies)
 *
 * Container layout (all integers big-endian):
 *   "EVC2"            4   magic
 *   version           1   currently 1
 *   iterations        4   PBKDF2-SHA256 iterations
 *   chunkSize         4   plaintext bytes per chunk
 *   salt             16   random per file
 *   baseIv           12   random per file
 *   chunk[0..n-1]         AES-GCM ciphertext + 16-byte tag, each at most chunkSize + 16 bytes
 *
 * Each chunk i uses IV = baseIv with its last 4 bytes XOR i, and authenticated
 * data = header || i (u32) || isFinal (u8). Binding the index and a final flag
 * means reordering, dropping, or truncating chunks, or editing the header,
 * fails authentication. Wrong passphrases also fail authentication (GCM tag),
 * instead of returning garbage as the legacy CryptoJS format could.
 *
 * Legacy CryptoJS ("U2FsdGVk...") files are detected by isV2() === false and
 * must be handled by the caller's legacy path.
 */

const MAGIC = [0x45, 0x56, 0x43, 0x32]; // "EVC2"
const VERSION = 1;
const HEADER_LEN = 4 + 1 + 4 + 4 + 16 + 12;
const TAG_LEN = 16;

export const DEFAULT_ITERATIONS = 600000;
export const DEFAULT_CHUNK_SIZE = 1024 * 1024;

const subtle = () => crypto.subtle;

async function deriveKey(passphrase, salt, iterations) {
  const material = await subtle().importKey('raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return subtle().deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function chunkIv(baseIv, index) {
  const iv = baseIv.slice();
  const view = new DataView(iv.buffer, iv.byteOffset, iv.byteLength);
  view.setUint32(8, (view.getUint32(8) ^ index) >>> 0);
  return iv;
}

function chunkAad(header, index, isFinal) {
  const aad = new Uint8Array(header.length + 5);
  aad.set(header);
  const view = new DataView(aad.buffer);
  view.setUint32(header.length, index);
  aad[header.length + 4] = isFinal ? 1 : 0;
  return aad;
}

/** True if the bytes start with the EVC2 magic. Accepts ArrayBuffer or Uint8Array. */
export function isV2(data) {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  return bytes.length >= HEADER_LEN && MAGIC.every((b, i) => bytes[i] === b);
}

/**
 * Encrypt a Blob/File.
 * @returns {Promise<Blob>} container bytes (type application/octet-stream)
 */
export async function encryptBlob(blob, passphrase, { iterations = DEFAULT_ITERATIONS, chunkSize = DEFAULT_CHUNK_SIZE } = {}) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const baseIv = crypto.getRandomValues(new Uint8Array(12));

  const header = new Uint8Array(HEADER_LEN);
  const view = new DataView(header.buffer);
  header.set(MAGIC, 0);
  header[4] = VERSION;
  view.setUint32(5, iterations);
  view.setUint32(9, chunkSize);
  header.set(salt, 13);
  header.set(baseIv, 29);

  const key = await deriveKey(passphrase, salt, iterations);
  const chunkCount = Math.max(1, Math.ceil(blob.size / chunkSize));
  const parts = [header];

  for (let i = 0; i < chunkCount; i++) {
    const plain = await blob.slice(i * chunkSize, (i + 1) * chunkSize).arrayBuffer();
    const sealed = await subtle().encrypt(
      { name: 'AES-GCM', iv: chunkIv(baseIv, i), additionalData: chunkAad(header, i, i === chunkCount - 1) },
      key,
      plain
    );
    parts.push(new Uint8Array(sealed));
  }

  return new Blob(parts, { type: 'application/octet-stream' });
}

/**
 * Decrypt container bytes.
 * @param {ArrayBuffer|Uint8Array} data
 * @returns {Promise<Uint8Array>} plaintext
 * @throws if the passphrase is wrong or the data was modified/truncated
 */
export async function decryptBytes(data, passphrase) {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  if (!isV2(bytes)) throw new Error('Not an EVC2 container');

  const header = bytes.slice(0, HEADER_LEN);
  const view = new DataView(header.buffer);
  if (header[4] !== VERSION) throw new Error(`Unsupported container version ${header[4]}`);
  const iterations = view.getUint32(5);
  const chunkSize = view.getUint32(9);
  const salt = header.slice(13, 29);
  const baseIv = header.slice(29, 41);

  // Bound work from untrusted headers.
  if (iterations < 100000 || iterations > 5000000 || chunkSize < 1024 || chunkSize > 64 * 1024 * 1024) {
    throw new Error('Invalid container parameters');
  }

  const key = await deriveKey(passphrase, salt, iterations);
  const sealedChunk = chunkSize + TAG_LEN;
  const body = bytes.subarray(HEADER_LEN);
  const chunkCount = Math.max(1, Math.ceil(body.length / sealedChunk));
  const out = [];

  for (let i = 0; i < chunkCount; i++) {
    const sealed = body.subarray(i * sealedChunk, (i + 1) * sealedChunk);
    if (sealed.length < TAG_LEN) throw new Error('Truncated data');
    try {
      const plain = await subtle().decrypt(
        { name: 'AES-GCM', iv: chunkIv(baseIv, i), additionalData: chunkAad(header, i, i === chunkCount - 1) },
        key,
        sealed
      );
      out.push(new Uint8Array(plain));
    } catch {
      throw new Error('Decryption failed: wrong key or the file was modified');
    }
  }

  const total = out.reduce((n, c) => n + c.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of out) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}
